/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1780358400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jun 02 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "elasticsearch-destination",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Elasticsearch",
          "depth": 0,
          "id": "connect-to-elasticsearch"
        }, {
          "value": "Authenticate with an Elasticsearch Cloud ID and API key",
          "depth": 1,
          "id": "authenticate-with-an-elasticsearch-cloud-id-and-api-key"
        }, {
          "value": "Authenticate with basic authentication",
          "depth": 1,
          "id": "authenticate-with-basic-authentication"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing to an index",
          "depth": 1,
          "id": "syncing-to-an-index"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Index documents"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to an Elasticsearch index"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.elastic.co/guide/en/elasticsearch/reference/8.7/docs-index_.html",
                children: "Index API"
              })
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-elasticsearch",
        children: "Connect to Elasticsearch"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Elasticsearch"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Elasticsearch either with an Elasticsearch ", _jsx(_components.strong, {
          children: "Cloud ID"
        }), " and ", _jsx(_components.strong, {
          children: "API key"
        }), " or via optional ", _jsx(_components.a, {
          href: "https://www.elastic.co/guide/en/elasticsearch/reference/current/http-clients.html",
          children: "basic authentication"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-an-elasticsearch-cloud-id-and-api-key",
        children: "Authenticate with an Elasticsearch Cloud ID and API key"
      }), "\n", _jsxs(_components.p, {
        children: ["You can find your ", _jsx(_components.a, {
          href: "https://www.elastic.co/guide/en/cloud/current/ec-cloud-id.html",
          children: "Cloud ID"
        }), " in your Elasticsearch deployment. To generate an API key, follow these steps in the ", _jsx(_components.a, {
          href: "https://www.elastic.co/guide/en/elasticsearch/reference/current/security-api-create-api-key.html",
          children: "Elasticsearch docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-basic-authentication",
        children: "Authenticate with basic authentication"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Protocol"
          }), ": Select the protocol used for connecting to Elasticsearch. This is either ", _jsx(_components.code, {
            children: "HTTP"
          }), " or ", _jsx(_components.code, {
            children: "HTTPS"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Elasticsearch server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Elasticsearch server's HTTP API. The default is 9200, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal Elasticsearch login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Elasticsearch destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Elasticsearch destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-to-an-index",
        children: "Syncing to an index"
      }), "\n", _jsxs(_components.p, {
        children: ["An Elasticsearch ", _jsx(_components.a, {
          href: "https://www.elastic.co/blog/what-is-an-elasticsearch-index",
          children: "index"
        }), " is the place where the data used by a search engine is stored. You can use Hightouch to update individual ", _jsx(_components.a, {
          href: "https://www.elastic.co/guide/en/elasticsearch/reference/current/documents-indices.html#:~:text=Elasticsearch%20is%20a%20distributed%20document,been%20serialized%20as%20JSON%20documents.",
          children: "documents"
        }), " in an index."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to documents in Elasticsearch, you need to select a model column and corresponding Elasticsearch field. You can match on any Elasticsearch field."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync data to both existing fields and fields that don't yet exist in Elasticsearch. If you send data to a field that doesn't exist yet, Hightouch adds the field and automatically detects its type."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the document in Elasticsearch"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Keep the document in Elasticsearch, but set mapped fields to empty"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Remove the document from Elasticsearch"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Elasticsearch, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Elasticsearch"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Connect to Elasticsearch on AWS"
        })
      }), "\n", _jsx(_components.p, {
        children: "To connect to an AWS hosted Elasticsearch, connect to Elastic Cloud where the instance has a Cloud ID, then generate an API key. If you can't connect via this method, use the basic authentication method to connect with username and password."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["There may be compatibility issues across different versions of Elasticsearch. If you receive an error when you test the connection, please ", _jsx(PylonLink, {
            pylonText: "Hello, I would like to request support for the version of Elasticsearch I am using.",
            linkText: "reach out to customer support"
          }), " for support for your specific version."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you are running Elasticsearch via Amazon OpenSearch Service, connect directly via the ", _jsx(_components.a, {
          href: "/destinations/opensearch",
          children: "OpenSearch destination"
        })]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
